import { type ClockMonthNote, clockDaysOf, formatUtcOffset } from '@felix/domain';
import type { StatusTone } from '../../StatusBadge.js';
import { daySegments } from './rest-hours.js';
import { asClock, daySpan, type ShipTime, wallRanges } from './ship-clock.js';
import type { PersonnelSource } from './source.js';
import type { CrewRestDay, RestMonth, RestMonthStatusName, WorkPeriod } from './types.js';

/** `YYYY-MM` ayını `n` ay kaydırır. */
export function shiftMonth(month: string, n: number): string {
  const [year, m] = month.split('-').map(Number) as [number, number];
  const date = new Date(Date.UTC(year, m - 1 + n, 1));
  return date.toISOString().slice(0, 7);
}

/** Ayın ilk ve son günü. */
export function monthRange(month: string): { from: string; to: string } {
  const [year, m] = month.split('-').map(Number) as [number, number];
  const last = new Date(Date.UTC(year, m, 0)).getUTCDate();
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` };
}

/**
 * Monthly sekmesinin verisi (ADR 0021): ayın kaydı, kişinin ay boyunca günleri ve dönemleri;
 * gelecek ayın günü ve dönemi okunmaz. Seçili günün liste üyeliğine bakmaz (ortak notu P1,
 * 2026-11-02): ayrılan kişinin geçmiş ayları da açılır, imzalanır ve onaylanır [MLC A2.3 §12].
 */
export async function loadRestMonth(
  source: Pick<PersonnelSource, 'restMonth' | 'restHours' | 'workPeriods'>,
  input: { vesselId: string; userId: string; month: string; today: string },
): Promise<{ record: RestMonth; days: CrewRestDay[]; periods: WorkPeriod[] }> {
  const { vesselId, userId, month, today } = input;
  const { from, to } = monthRange(month);
  const last = to < today ? to : today;
  const [record, crew, periods] = await Promise.all([
    source.restMonth(vesselId, userId, month),
    from <= today
      ? source.restHours(vesselId, from, last).then((r) => r.crew)
      : Promise.resolve([]),
    from <= today ? source.workPeriods(vesselId, userId, from, last) : Promise.resolve([]),
  ]);
  return { record, days: crew.find((c) => c.userId === userId)?.days ?? [], periods };
}

export interface MonthRow {
  date: string;
  /** Tarih çizgisinde iki kez yaşanan günün oluşumu (ADR 0065 §7): iki satır. */
  occurrence?: 1 | 2;
  /** Günün gerçek süresi (dakika): 1440, saat değişen günde 1380/1500 vb.; atlanan günde 0. */
  minutes?: number;
  /** Tarih çizgisinde atlanan gün: satır taralı, değer yok. */
  skippedDay?: boolean;
  /** İleri almada yaşanmayan duvar saati aralıkları (ızgarada taralı). */
  notLived?: { from: number; to: number }[];
  /**
   * Çalışma aralıkları, ILO/IMO ızgarasının duvar saatiyle (0–1440 dakika): tekrar eden saatin iki
   * oluşumu aynı sütunda birleşir; süreler gerçek süredir (`workHours`).
   */
  work: { from: number; to: number; activity: string | null }[];
  workHours: number | null;
  restHours: number | null;
  rest24h: number | null;
  rest7d: number | null;
  status: CrewRestDay['status'] | null;
  /** Acil durum istisnası [MLC A2.3 §14]: kaptan onayda imzalar. */
  exception: boolean;
  /** Günün bulguları (kural, ağırlık, istisna); PDF açıklamaları için (ADR 0024 #12). */
  findings: { rule: string; severity: 'violation' | 'warning'; exception: boolean }[];
  /** Bugünden sonraki gün (içinde bulunulan ay): henüz yaşanmadı, "kayıt yok" değil (G3-06). */
  upcoming: boolean;
}

/**
 * Aylık tablo (ILO/IMO kayıt biçimi): ayın her günü bir satır (tarih çizgisinde iki kez yaşanan
 * gün iki satır; ADR 0065 §7). Değerler sunucudaki gün gün rapordan (gerçek süre); çalışma
 * aralıkları dönemlerden gemi saatinin duvar saatiyle. Raporda olmayan gün (görev dışı) boş.
 * `today` (gemi yerel bugünü) verilirse ondan sonraki günler `upcoming` satırdır: bant ve değer
 * yoktur (M6f G3-06).
 */
export function monthTable(
  days: readonly CrewRestDay[],
  periods: readonly WorkPeriod[],
  timeZone: ShipTime,
  month: string,
  today?: string,
): MonthRow[] {
  const keyOf = (date: string, occurrence: number | undefined) => `${date}#${occurrence ?? 1}`;
  const byDate = new Map(days.map((d) => [keyOf(d.date, d.occurrence), d]));
  const { from, to } = monthRange(month);
  const clock = asClock(timeZone);
  const rows: MonthRow[] = [];
  for (let date = from; date <= to; ) {
    const upcoming = today !== undefined && date > today;
    const span = daySpan(clock, date);
    const occurrences = clockDaysOf(clock, date);
    const segments = upcoming || span.skipped ? [] : daySegments(periods, clock, date);
    for (const day of occurrences) {
      const report = upcoming || span.skipped ? undefined : byDate.get(keyOf(date, day.occurrence));
      // Oluşumun gerçek dakikaları gün başından
      const lo = Math.round((Date.parse(day.start) - span.start) / 60_000);
      const hi = lo + day.minutes;
      const work = segments
        .filter((seg) => seg.kind === 'work' && seg.to > lo && seg.from < hi)
        .flatMap((seg) =>
          wallRanges(span, [{ from: Math.max(seg.from, lo), to: Math.min(seg.to, hi) }]).map(
            (range) => ({ ...range, activity: seg.activity }),
          ),
        );
      rows.push(
        monthRow(date, upcoming, report, {
          ...(day.occurrence ? { occurrence: day.occurrence } : {}),
          minutes: day.minutes,
          skippedDay: span.skipped,
          notLived: span.timeline.skipped.map((g) => ({ from: g.wallFrom, to: g.wallTo })),
          work,
        }),
      );
    }
    const next = new Date(Date.parse(`${date}T00:00:00.000Z`) + 86_400_000);
    date = next.toISOString().slice(0, 10);
  }
  return rows;
}

function monthRow(
  date: string,
  upcoming: boolean,
  report: CrewRestDay | undefined,
  clock: Pick<MonthRow, 'occurrence' | 'minutes' | 'skippedDay' | 'notLived' | 'work'>,
): MonthRow {
  return {
    date,
    upcoming,
    ...clock,
    workHours: report?.workHours ?? null,
    restHours: report?.restHours ?? null,
    rest24h: report?.rest24h ?? null,
    rest7d: report?.rest7d ?? null,
    status: report?.status ?? null,
    exception: report?.findings.some((f) => f.exception === true) ?? false,
    findings: (report?.findings ?? []).map((f) => ({
      rule: f.rule,
      severity: f.severity,
      exception: f.exception === true,
    })),
  };
}

const TONES: Readonly<Record<RestMonthStatusName, StatusTone>> = {
  draft: 'neutral',
  pending: 'info',
  approved: 'success',
  returned: 'danger',
  reapproval_pending: 'warning',
};

export function monthStatusTone(status: RestMonthStatusName): StatusTone {
  return TONES[status];
}

/**
 * Aylık kaydın gemi saati notu (ADR 0065 §7), ör. "Ship's clocks retarded by 60 minutes on
 * 16.10.2026 00:00 (UTC+03:00 → UTC+02:00)": ekranda ve PDF'te aynı metin. Zaman eski saatle
 * (değişiklik anında saatin gösterdiği). `t` `restHours.clock` anahtarlarını çözer.
 */
export function clockNoteText(
  note: ClockMonthNote,
  t: (key: string, options?: Record<string, unknown>) => string,
  formatDate: (date: string) => string,
): string {
  if (note.kind !== 'change') {
    return t(`restHours.clock.${note.kind === 'repeated' ? 'noteRepeated' : 'noteSkipped'}`, {
      date: formatDate(note.date),
    });
  }
  return t(`restHours.clock.${note.changeMin < 0 ? 'noteRetarded' : 'noteAdvanced'}`, {
    minutes: Math.abs(note.changeMin),
    date: formatDate(note.wallBefore.slice(0, 10)),
    time: note.wallBefore.slice(11, 16),
    from: formatUtcOffset(note.oldOffsetMin),
    to: formatUtcOffset(note.newOffsetMin),
  });
}
